import clsx from 'clsx';
import Image from 'next/image';
import { FC, useEffect, useRef, useState } from 'react';

import { SECTIONS_IDS } from '../../../constants/sections';
import { Container } from '../common/Container';
import { Flex } from '../common/Flex';
import { Slider } from '../common/Slider';

import { IntegrationSection } from './components/IntegrationSection';
import { ProcessorSection } from './components/ProcessorSection';
import styles from './PaymentsBlock.module.scss';

const REVENUE_ANALYTICS_IMAGE = {
  src: '/assets/revenue-analytics.png',
  alt: 'Revenue & Analytics',
  width: 1024,
  height: 785,
} as const;

const MAP_IMAGE = {
  src: '/assets/map.png',
  alt: 'Integrations',
  width: 1008,
  height: 664,
} as const;

export const PaymentsBlock: FC = () => {
  const rootRef = useRef<HTMLDivElement>(null);
  const [activeImage, setActiveImage] = useState<'payments' | 'integrations' | ''>('payments');
  const [isFixed, setIsFixed] = useState(false);

  const slides = [
    {
      id: 'ProcessorSection',
      content: <ProcessorSection />,
    },
    {
      id: 'IntegrationSection',
      content: <IntegrationSection />,
    },
  ];

  useEffect(() => {
    if (!rootRef.current) return;

    const handleScroll = () => {
      const rootRect = rootRef.current!.getBoundingClientRect();
      const viewportCenter = window.innerHeight / 2;

      const shouldFix =
        rootRect.top < (window.innerHeight - rootRect.height * 0.4) / 2 &&
        rootRect.bottom > (window.innerHeight - rootRect.height / 2) / 2 + rootRect.height / 2;

      setIsFixed(shouldFix);

      const isBlockInViewport = rootRect.top < window.innerHeight && rootRect.bottom > 0;

      if (isBlockInViewport) {
        const blockTop = rootRect.top;
        const blockHeight = rootRect.height;
        const relativePos = viewportCenter - blockTop;

        const neutralZoneRatio = 0.2;
        const neutralZoneHeight = blockHeight * neutralZoneRatio;
        const neutralStart = (blockHeight - neutralZoneHeight) / 2;
        const neutralEnd = neutralStart + neutralZoneHeight;

        if (relativePos < neutralStart) {
          setActiveImage('payments');
        } else if (relativePos > neutralEnd) {
          setActiveImage('integrations');
        } else {
          setActiveImage('');
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <Container id={SECTIONS_IDS.products}>
      <div className={styles.root} ref={rootRef}>
        <Flex direction={'column'} gap={40}>
          <ProcessorSection />

          <IntegrationSection />
        </Flex>

        <div className={styles.rightSection}>
          <div
            className={clsx(
              styles.gifWrapper,
              isFixed && styles.fixed,
              activeImage === 'payments' && styles.processor,
              activeImage === 'integrations' && styles.integrations
            )}
          >
            <div className={clsx(styles.gif, styles.revenueAnalytics, activeImage === 'payments' && styles.visible)}>
              <Image
                src={REVENUE_ANALYTICS_IMAGE.src}
                alt={REVENUE_ANALYTICS_IMAGE.alt}
                layout="fill"
                objectFit="contain"
                objectPosition="left top"
                sizes="(max-width: 959px) 320px, 560px"
              />
            </div>
            <div className={clsx(styles.gif, styles.map, activeImage === 'integrations' && styles.visible)}>
              <Image
                src={MAP_IMAGE.src}
                alt={MAP_IMAGE.alt}
                layout="fill"
                objectFit="contain"
                objectPosition="left top"
                sizes="(max-width: 959px) 483px, 584px"
              />
            </div>
          </div>
        </div>
      </div>

      <div className={styles.mobileRoot}>
        <Flex direction={'column'} gap={40} alignItems={'center'}>
          <div className={clsx(styles.gif, styles.revenueAnalytics)}>
            <Image
              src={REVENUE_ANALYTICS_IMAGE.src}
              alt={REVENUE_ANALYTICS_IMAGE.alt}
              layout="fill"
              objectFit="contain"
              sizes="(max-width: 959px) 320px, 560px"
            />
          </div>

          <div className={styles.slider}>
            <Slider items={slides} />
          </div>

          <div className={styles.gifWrapper}>
            <div className={clsx(styles.gif, styles.map)}>
              <Image
                src={MAP_IMAGE.src}
                alt={MAP_IMAGE.alt}
                layout="fill"
                objectFit="contain"
                sizes="(max-width: 959px) 483px, 584px"
              />
            </div>
          </div>
        </Flex>
      </div>
    </Container>
  );
};
